/**
 * Ecom Cart Drawer Logic
 * Handles opening/closing and cart interactions for the v2 cart drawer
 */
(function() {
  const selectors = {
    drawer: '.cart-drawer-v2',
    overlay: '.cart-drawer-v2__overlay',
    closeButton: '.cart-drawer-v2__close-button',
    qtyInput: '.quantity-v2__input',
    qtyButton: '.quantity-v2__button',
    removeButton: '.cart-item-v2__remove, [data-action="remove-item"]',
    form: '#cart-form'
  };

  class EcomCart {
    constructor() {
      this.init();
    }

    init() {
      this.drawer = document.querySelector(selectors.drawer);
      if (!this.drawer) return;

      this.bindEvents();
      
      // Expose API
      window.EcomCart = this;
      
      console.log('✅ EcomCart initialized');
    }

    bindEvents() {
      // Intercept add to cart forms
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form.matches('[action="/cart/add"]')) {
          e.preventDefault();
          this.handleAddToCart(form);
        }
      });
      
      // Open/Close
      document.body.addEventListener('click', (e) => {
        const target = e.target;
        
        // Open triggers
        if (target.closest('[data-action="open-cart-drawer"]')) {
          e.preventDefault();
          this.open();
        }
        
        // Close triggers
        if (target.closest('[data-action="close-cart-drawer"]')) {
          e.preventDefault();
          this.close();
        }
      });

      // Cart Interactions
      this.drawer.addEventListener('click', (e) => {
        const target = e.target;
        
        // Quantity Buttons
        const qtyBtn = target.closest(selectors.qtyButton);
        if (qtyBtn) {
          e.preventDefault();
          e.stopPropagation(); // Stop theme.js from also handling
          this.handleQuantityClick(qtyBtn);
        }
        
        // Remove Button
        const removeBtn = target.closest(selectors.removeButton);
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation(); // Stop theme.js from also handling
          this.handleRemoveClick(removeBtn);
        }
      }, true); // Use capture phase to handle before theme.js
      
      // Listen for cart:refresh events to reload the drawer content
      document.documentElement.addEventListener('cart:refresh', () => {
        this.refreshCart();
      });
      
      // Listen for product:added from other sources (theme, etc.)
      document.addEventListener('product:added', (e) => {
        // Only handle if not from our own handleAddToCart
        if (e.detail && !e.detail.fromEcomCart) {
          this.open();
          this.refreshCart();
        }
      });
      
      // Listen for cart:updated events (from theme or other scripts)
      document.addEventListener('cart:updated', (e) => {
        console.log('🔄 EcomCart: cart:updated received', e.detail);
        this.refreshCart();
        // If source is 'add', open the drawer
        if (e.detail && e.detail.source === 'add') {
          this.open();
        }
      });
      
      // Listen for cart:change events (from theme or other scripts)
      document.addEventListener('cart:change', (e) => {
        console.log('🔄 EcomCart: cart:change received', e.detail);
        this.refreshCart();
      });
    }

    open() {
      if (this.drawer) {
        this.drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('no-scroll'); // Add lock scroll if theme supports it
        
        // Dispatch event for other scripts
        this.drawer.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
      }
    }

    close() {
      if (this.drawer) {
        this.drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('no-scroll');
        
        // Dispatch event
        this.drawer.dispatchEvent(new CustomEvent('cart-drawer:close', { bubbles: true }));
      }
    }
    
    handleQuantityClick(btn) {
      const action = btn.dataset.action;
      const lineIndex = btn.dataset.lineIndex;
      const input = this.drawer.querySelector(`${selectors.qtyInput}[data-line-index="${lineIndex}"]`);
      
      if (!input) return;
      
      let value = parseInt(input.value);
      if (action === 'increase-quantity') {
        value++;
      } else if (action === 'decrease-quantity') {
        value = Math.max(0, value - 1);
      }
      
      input.value = value;
      
      // Update cart directly via Shopify API
      this.updateCartLine(lineIndex, value);
    }
    
    handleRemoveClick(btn) {
      const lineIndex = btn.dataset.lineIndex;
      
      // Update cart directly via Shopify API
      this.updateCartLine(lineIndex, 0);
    }
    
    // Handle add to cart form submission
    handleAddToCart(form) {
      const formData = new FormData(form);
      
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: formData
      })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => { throw err; });
        }
        return response.json();
      })
      .then(item => {
        console.log('✅ Product added to cart:', item);

        // Refresh cart and open drawer
        this.refreshCart();
        this.open();

        // Dispatch event for other scripts (mark it as from EcomCart to avoid loops)
        document.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { item, fromEcomCart: true }
        }));
      })
      .catch(error => {
        console.error('❌ Error adding to cart:', error);
        // Only show a generic alert for non-limit errors (limit errors show a toast instead)
        if (!error.description) {
          alert('Impossible d\'ajouter le produit au panier. Veuillez réessayer.');
        }
      });
    }
    
    // Update cart line via Shopify API
    updateCartLine(line, quantity) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          line: parseInt(line),
          quantity: parseInt(quantity)
        })
      })
      .then(response => response.json())
      .then(cart => {
        console.log('✅ Cart updated:', cart);
        this.refreshCart();
      })
      .catch(error => {
        console.error('❌ Error updating cart:', error);
        // Refresh anyway to show correct state
        this.refreshCart();
      });
    }
    
    // Function to refresh cart HTML
    refreshCart() {
      console.log('🔄 EcomCart: Refreshing cart drawer...');
      
      // Fetch the header section which contains the cart drawer
      fetch('/?section_id=header') 
        .then(response => response.text())
        .then(html => {
          // Parse the response
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Find the new drawer content
          const newDrawer = doc.querySelector(selectors.drawer);
          const newContent = newDrawer ? newDrawer.querySelector('.cart-drawer-v2__inner') : null;
          
          if (newContent && this.drawer) {
             const currentContent = this.drawer.querySelector('.cart-drawer-v2__inner');
             if (currentContent) {
               currentContent.replaceWith(newContent);
               console.log('✅ EcomCart: Cart drawer content refreshed');
               
               // Update header cart count
               const newCartCount = doc.querySelector('.header__cart-count');
               const currentCartCount = document.querySelector('.header__cart-count');
               if (newCartCount && currentCartCount) {
                 currentCartCount.textContent = newCartCount.textContent;
                 console.log('✅ EcomCart: Cart count updated to', newCartCount.textContent);
               }
               
               // Trigger cart rerendered event for other scripts to update display
               document.dispatchEvent(new CustomEvent('cart:rerendered', { bubbles: true }));
             }
          } else {
            console.warn('⚠️ EcomCart: Could not find new cart content to replace');
          }
        })
        .catch(err => console.error('❌ EcomCart: Error refreshing cart drawer:', err));
    }
  }

  // Initialize on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new EcomCart());
  } else {
    new EcomCart();
  }
})();
